/**
 * Sentry frontend SDK init.
 *
 * Mirrors `digipop-cloud/app/core/sentry.py` + `digipop-agent/agent/
 * sentry.py`: completely no-op until `VITE_SENTRY_DSN` is set, so the
 * SPA boots clean in dev where the DSN is intentionally empty and we
 * don't want every page load to phone home.
 *
 * The `beforeSend` filter drops handled HTTP client errors so the
 * Sentry quota only burns on real faults:
 * * 401 — bad token / expired session (silent-refresh handles it)
 * * 403 — forbidden by RBAC
 * * 404 — record missing
 * * 422 — validation error (the form already tells the operator)
 * * 429 — rate-limited (rare, surfaced via toast already)
 *
 * Network errors get one tag (`network: true`) but pass through so we
 * can debug intermittent backend issues from the dashboards.
 */
import * as Sentry from '@sentry/react';

interface EnvLike {
  VITE_SENTRY_DSN?: string;
  VITE_APP_ENV?: string;
  VITE_BUILD_SHA?: string;
  VITE_SENTRY_TRACES_SAMPLE_RATE?: string;
}

const SILENCED_HTTP_STATUSES = new Set([401, 403, 404, 422, 429]);

export function initSentry(env: EnvLike = import.meta.env): boolean {
  const dsn = (env.VITE_SENTRY_DSN ?? '').trim();
  if (!dsn) return false;

  Sentry.init({
    dsn,
    environment: env.VITE_APP_ENV ?? 'unknown',
    release: env.VITE_BUILD_SHA ?? 'dev',
    sendDefaultPii: false,
    tracesSampleRate: Number.parseFloat(env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? '0') || 0,
    integrations: [Sentry.browserTracingIntegration()],
    beforeSend(event, hint) {
      const exc = hint?.originalException as
        | { response?: { status?: number }; isAxiosError?: boolean; code?: string }
        | undefined;
      const status = exc?.response?.status;
      if (status !== undefined && SILENCED_HTTP_STATUSES.has(status)) return null;
      if (exc?.isAxiosError && !exc.response) {
        // Network failure (DNS, timeout, refused). Pass through so a
        // pattern of these surfaces in the dashboards, but tag them so
        // they're easy to separate from API faults.
        event.tags = { ...event.tags, network: 'true' };
      }
      return event;
    },
  });
  Sentry.setTag('surface', 'digipop-admin');
  return true;
}
